<template>
  <div class="color-report-detail-container">
    <!-- 1. 顶部图像预览 -->
    <div class="report-header-images pdf-atomic-block">
      <div class="labeled-image-container">
        <div class="image-label-wrapper">
          <el-tag type="primary" effect="dark">{{
            $t("report.originalImage")
          }}</el-tag>
        </div>
        <div class="image-wrapper">
          <div v-if="convertingMap.original" class="image-loading">
            <i class="el-icon-loading"></i>
          </div>
          <div v-else-if="conversionErrors.original" class="image-error">
            {{ $t("error.operationFailed") }}
          </div>
          <img
            v-else
            :src="
              getDisplayUrl(
                'original',
                reportResultData && reportResultData.image_url
              )
            "
            crossorigin="anonymous"
            alt="Original Image Preview"
          />
        </div>
      </div>
      <div class="labeled-image-container">
        <div class="image-label-wrapper">
          <el-tag type="primary" effect="dark">{{
            $t("report.roiImage")
          }}</el-tag>
        </div>
        <div class="image-wrapper">
          <div v-if="convertingMap.roi" class="image-loading">
            <i class="el-icon-loading"></i>
          </div>
          <div v-else-if="conversionErrors.roi" class="image-error">
            {{ $t("error.operationFailed") }}
          </div>
          <img
            v-else
            :src="
              getDisplayUrl(
                'roi',
                reportResultData && reportResultData.image_roi_url
              )
            "
            crossorigin="anonymous"
            alt="ROI Image"
          />
        </div>
      </div>
    </div>

    <!-- 2. 分析结果 (Analysis result) -->
    <div class="pdf-atomic-block">
      <h4 id="analysis-result" class="section-title">
        {{ $t("report.analysis_result_title") }}
      </h4>
      <div class="side-by-side-tables">
        <el-table
          :data="analysisResultLeft"
          style="width: 100%"
          border
          class="custom-table"
        >
          <el-table-column prop="item" label="Item"></el-table-column>
          <el-table-column prop="value" label="Value"></el-table-column>
        </el-table>
        <el-table
          :data="analysisResultRight"
          style="width: 100%"
          border
          class="custom-table"
        >
          <el-table-column prop="item" label="Item"></el-table-column>
          <el-table-column prop="value" label="Value"></el-table-column>
        </el-table>
      </div>
      <div class="content-divider"></div>
    </div>

    <div class="pdf-atomic-block">
      <el-table
        :data="reportResultData.result.analysis_result_1_form"
        style="width: 100%"
        border
        class="custom-table"
      >
        <el-table-column prop="item" label="Item"></el-table-column>
        <el-table-column prop="mean" label="Mean"></el-table-column>
        <el-table-column prop="sigma" label="Sigma"></el-table-column>
        <el-table-column prop="max" label="Max"></el-table-column>
      </el-table>
    </div>

    <!-- 3. 坐标值 (Coordinate values) -->
    <div class="pdf-atomic-block">
      <h4 id="coordinate-value" class="section-title">
        {{ $t("report.coordinate_value_title") }}
      </h4>
      <h5 class="sub-section-title">{{ $t("report.coordinate_chart") }}</h5>
      <div class="chart-container">
        <p class="chart-title-text">
          {{ reportResultData.result.color_coordinate_chart_title }}
        </p>
        <div class="echart-wrapper">
          <div class="chart-stack-wrapper">
            <!-- 图表1：底层，只画网格和坐标轴 -->
            <v-chart
              class="echart-on-top"
              :option="gridChartOption"
              autoresize
            />

            <!-- 图层2：中间，显示背景图 -->
            <img
              :src="coordinateValuesImage"
              class="chart-background-image"
              alt="Color Coordinate Background"
            />

            <!-- 图表3：顶层，只画数据点和线 -->
            <v-chart
              class="echart-on-top"
              ref="cvChartCombined"
              :option="dataChartOption"
              autoresize
            />
          </div>
        </div>
      </div>
    </div>

    <div class="pdf-atomic-block">
      <h5 class="sub-section-title">
        {{ $t("report.coordinate_block_chart") }}
      </h5>
      <div class="chart-container">
        <img
          class="color-block-image"
          :src="reportResultData.image_roi_url"
          crossorigin="anonymous"
          alt="Color Block Area Chart"
        />
      </div>
    </div>

    <div class="pdf-atomic-block">
      <h5 class="sub-section-title">
        {{ $t("report.coordinate_values") }}
      </h5>
      <el-table
        :data="reportResultData.result.color_coordinate_values_reference_form"
        style="width: 100%"
        border
        class="custom-table"
      >
        <el-table-column prop="item" label="Item" width="80">
          <template #default="scope">
            {{ scope.row.item + 1 }}
          </template>
        </el-table-column>
        <el-table-column
          prop="reference_a"
          :label="$t('report.reference_value_a')"
        ></el-table-column>
        <el-table-column
          prop="calculated_a"
          :label="$t('report.calculated_value_a')"
        ></el-table-column>
        <el-table-column
          prop="reference_b"
          :label="$t('report.reference_value_b')"
        ></el-table-column>
        <el-table-column
          prop="calculated_b"
          :label="$t('report.calculated_value_b')"
        ></el-table-column>
      </el-table>
    </div>

    <!-- 4. 色差 (Aberration) -->
    <div class="pdf-atomic-block">
      <h4 class="section-title">{{ $t("report.aberration") }}</h4>
      <el-table
        :data="reportResultData.result.aberration_form"
        style="width: 100%"
        border
        class="custom-table"
      >
        <el-table-column prop="item" label="Item" width="80">
          <template #default="scope">
            {{ scope.row.item + 1 }}
          </template>
        </el-table-column>
        <el-table-column prop="delta_e00" label="Δ_E00"></el-table-column>
        <el-table-column
          prop="delta_c00_corr"
          label="Δ_C00corr"
        ></el-table-column>
        <el-table-column prop="delta_e_ab" label="Δ_Eab"></el-table-column>
        <el-table-column prop="delta_c_ab" label="Δ_Cab"></el-table-column>
        <el-table-column
          prop="delta_c_ab_corr"
          label="Δ_Cabcorr"
        ></el-table-column>
      </el-table>
    </div>

    <!-- 5. SNR -->
    <div class="pdf-atomic-block">
      <h4 id="snr" class="section-title">{{ $t("report.SNR_title") }}</h4>
      <h5 class="sub-section-title">{{ $t("report.fitting_curve") }}</h5>
      <div class="chart-container fitting-curve-container">
        <div class="chart-inner-wrapper">
          <v-chart
            class="echart"
            :option="fittingCurveChartOption"
            ref="SNR_Chart1"
            autoresize
          />
        </div>
      </div>
    </div>

    <div class="pdf-atomic-block">
      <h5 class="sub-section-title">{{ $t("report.SNR_plot") }}</h5>
      <div class="chart-container">
        <div class="chart-inner-wrapper">
          <v-chart
            class="echart"
            :option="snrPlotChartOption"
            ref="SNR_Chart2"
            autoresize
          />
        </div>
      </div>
    </div>

    <div class="pdf-atomic-block">
      <h5 class="sub-section-title">{{ $t("report.SNR_value") }}</h5>
      <el-table
        :data="reportResultData.result.snr_value_form"
        style="width: 100%"
        border
        class="custom-table"
      >
        <el-table-column prop="item" label="Item" width="80"
          ><template #default="scope">
            {{ scope.row.item + 1 }}
          </template></el-table-column
        >
        <el-table-column prop="r" label="R"></el-table-column>
        <el-table-column prop="g" label="G"></el-table-column>
        <el-table-column prop="b" label="B"></el-table-column>
        <el-table-column prop="y" label="Y"></el-table-column>
      </el-table>
    </div>

    <!-- ** 回到顶部按钮 ** -->
    <el-backtop
      v-if="!isPrinting"
      :target="backtopTarget"
      :right="40"
      :bottom="40"
    />
  </div>
</template>

<script>
import { backtopMixin } from "@/mixins/backtopMixin";
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { ScatterChart, LineChart } from "echarts/charts";
import {
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
  MarkLineComponent,
  GraphicComponent,
} from "echarts/components";
import VChart from "vue-echarts";
import UTIF from "utif";

import coordinateValuesImage from "@/assets/coordinate-values.png";

use([
  CanvasRenderer,
  ScatterChart,
  LineChart,
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
  MarkLineComponent,
  GraphicComponent,
]);

export default {
  name: "ColorReproductionReportDetail",
  mixins: [backtopMixin],
  components: {
    VChart,
  },
  props: {
    reportData: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return {
      coordinateValuesImage: coordinateValuesImage,
      isPrinting: false,
      convertedImageUrls: {
        original: null,
        roi: null,
      },
      convertingMap: {
        original: false,
        roi: false,
      },
      conversionErrors: {},
    };
  },
  computed: {
    reportResultData() {
      if (this.reportData) {
        return this.reportData;
      }
      return null;
    },
    analysisResultLeft() {
      if (!this.reportResultData) return [];
      const items = ["chroma(%)", "gamma"];
      return this.reportResultData.result.analysis_result_2_form.filter((i) =>
        items.includes(i.item)
      );
    },
    analysisResultRight() {
      if (!this.reportResultData) return [];
      const items = ["SNR(dB)", "S(patch20)"];
      return this.reportResultData.result.analysis_result_3_form.filter((i) =>
        items.includes(i.item)
      );
    },

    baseChartConfig() {
      if (!this.reportResultData) return {};
      return {
        grid: { top: "5%", left: "10%", right: "10%", bottom: "15%" },
        xAxis: {
          type: "value",
          min: 0,
          max: 1,
          interval: 0.25,
        },
        yAxis: {
          type: "value",
          min: 0,
          max: 1,
          interval: 0.2,
          position: "left",
        },
      };
    },

    gridChartOption() {
      if (!this.reportResultData) return {};
      return {
        backgroundColor: "transparent",
        ...this.baseChartConfig,
        xAxis: {
          ...this.baseChartConfig.xAxis,
          splitLine: { show: true },
          minorSplitLine: { show: true },
          axisLine: {
            show: true,
            onZero: false,
            lineStyle: { color: "#000", width: 1.5 },
          },
          minorTick: { show: true, splitNumber: 5 },
          axisTick: { show: true },
        },
        yAxis: {
          ...this.baseChartConfig.yAxis,
          splitLine: { show: true },
          minorSplitLine: { show: true },
          axisLine: {
            show: true,
            onZero: false,
            lineStyle: { color: "#000", width: 1.5 },
          },
          minorTick: { show: true, splitNumber: 5 },
          axisTick: { show: true },
        },
      };
    },

    dataChartOption() {
      if (!this.reportResultData) return {};
      const { meas, ideal } =
        this.reportResultData.result.color_coordinate_chart_graph;
      const markLineData = meas.map((point, index) => [
        { coord: point },
        { coord: ideal[index] },
      ]);
      const isMobile = window.innerWidth <= 768;

      return {
        backgroundColor: "transparent",
        ...this.baseChartConfig,
        grid: {
          ...this.baseChartConfig.grid,
          bottom: isMobile ? "20%" : this.baseChartConfig.grid.bottom,
        },
        xAxis: {
          ...this.baseChartConfig.xAxis,
          show: false,
        },
        yAxis: {
          ...this.baseChartConfig.yAxis,
          show: false,
        },
        tooltip: {
          trigger: "item",
          formatter: (params) => {
            if (params.seriesName === "meas" || params.seriesName === "ideal") {
              const dataPoint = params.value;
              const dataIndex = params.dataIndex;
              return `<strong>${params.seriesName}</strong>: Item${
                dataIndex + 1
              }<br/>[${dataPoint[0]}, ${dataPoint[1]}]`;
            }
            return null;
          },
        },
        legend: {
          data: ["meas", "ideal"],
          bottom: "2%",
          left: "center",
          itemWidth: 15,
          itemHeight: 10,
          textStyle: { padding: [0, 15, 0, 2] },
        },
        series: [
          {
            name: "meas",
            type: "scatter",
            data: meas,
            symbolSize: 8,
            itemStyle: { color: "#00a0e9" },
          },
          {
            name: "ideal",
            type: "scatter",
            data: ideal,
            symbolSize: 8,
            itemStyle: { color: "#f7b500" },
          },
          {
            name: "Connections",
            type: "line",
            tooltip: {
              show: false,
            },
            markLine: {
              symbol: ["none", "none"],
              silent: true,
              lineStyle: { color: "#999", type: "solid" },
              data: markLineData,
            },
          },
        ],
      };
    },

    fittingCurveChartOption() {
      if (!this.reportResultData) return {};
      const chartData =
        this.reportResultData.result.gray_scale_fitting_curve_graph;
      if (!chartData || !chartData.series) return {};

      const { x_axis, series, x_range, y_range } = chartData;
      const seriesData = series.map((s) => ({
        ...s,
        data: s.data.map((y, index) => [x_axis[index], y]),
      }));

      return {
        color: ["#00a0e9", "#f7b500"],
        title: {
          text: "Gray scale fitting curve",
          left: "center",
          top: "3%",
          textStyle: {
            fontSize: 16,
            fontWeight: "normal",
          },
        },
        tooltip: { trigger: "axis" },
        legend: {
          data: ["1st order", "2nd order"],
          bottom: "2%",
          left: "center",
          itemWidth: 15,
          itemHeight: 10,
          textStyle: {
            padding: [0, 15, 0, 2],
          },
        },
        grid: { top: "12%", left: "8%", right: "8%", bottom: "24%" },
        xAxis: {
          type: "value",
          min: x_range[0],
          max: x_range[1],
          name: "Log Exposure (-Target density)",
          nameLocation: "middle",
          nameGap: 55,
          axisTick: { show: true },
          minorTick: { show: true },
          axisLine: {
            show: true,
            onZero: false,
            lineStyle: { color: "#000" },
          },
          splitLine: { show: true },
        },
        yAxis: {
          type: "value",
          min: y_range[0],
          max: y_range[1],
          name: "Log (Pixel level/255)",
          nameLocation: "middle",
          nameGap: 60,
          axisTick: { show: true },
          minorTick: { show: true },
          axisLine: {
            show: true,
            onZero: false,
            lineStyle: { color: "#000" },
          },
          splitLine: { show: true },
          minorSplitLine: { show: true },
        },
        series: seriesData.map((s, index) => ({
          ...s,
          name: ["1st order", "2nd order"][index],
          type: "line",
          symbol: "circle",
          symbolSize: 8,
          lineStyle: {
            type: "dashed",
          },
        })),
      };
    },

    snrPlotChartOption() {
      if (!this.reportResultData) return {};
      const result = this.reportResultData.result;
      const series = result.gray_scale_block_snr_plot_graph;
      return {
        color: ["#FF0000", "#90EE90", "#00a0e9", "#909399"],
        title: {
          text: "Gray-scale block SNR plot",
          left: "center",
          top: "3%",
          textStyle: {
            fontSize: 16,
            fontWeight: "normal",
          },
        },
        tooltip: { trigger: "axis" },
        legend: {
          data: ["R", "G", "B", "Y"],
          bottom: "2%",
          left: "center",
          itemWidth: 15,
          itemHeight: 10,
          textStyle: {
            padding: [0, 15, 0, 2],
          },
        },
        grid: { top: "12%", left: "8%", right: "8%", bottom: "15%" },
        xAxis: {
          type: "value",
          min: 0,
          max: result.gray_scale_block_snr_plot_x_range[1],
          axisTick: { show: true },
          minorTick: { show: true },
          axisLine: {
            show: true,
            onZero: true,
            lineStyle: { color: "#000" },
          },
          splitLine: { show: true },
        },
        yAxis: {
          type: "value",
          min: 0,
          max: result.gray_scale_block_snr_plot_y_range[1],
          name: "SNR(dB)(20*LOG10(S/N))",
          nameLocation: "middle",
          nameGap: 60,
          axisTick: { show: true },
          minorTick: { show: true },
          axisLine: {
            show: true,
            onZero: true,
            lineStyle: { color: "#000" },
          },
          splitLine: { show: true },
          minorSplitLine: { show: true },
        },
        series: series.map((s, index) => ({
          ...s,
          name: ["R", "G", "B", "Y"][index],
          type: "line",
          symbol: "none",
          lineStyle: {
            type: "dashed",
          },
        })),
      };
    },
  },
  watch: {
    "reportData.image_url": {
      handler(newUrl) {
        this.handleImageSourceChange("original", newUrl);
      },
      immediate: true,
    },
    "reportData.image_roi_url": {
      handler(newUrl) {
        this.handleImageSourceChange("roi", newUrl);
      },
      immediate: true,
    },
    // 监听reportResultData的变化，确保图片URL变化时触发转换
    reportResultData: {
      handler(newData) {
        if (newData) {
          if (newData.image_url) {
            this.handleImageSourceChange("original", newData.image_url);
          }
          if (newData.image_roi_url) {
            this.handleImageSourceChange("roi", newData.image_roi_url);
          }
        }
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    isTifUrl(url) {
      if (!url) return false;
      const lower = String(url).toLowerCase();
      return lower.endsWith(".tif") || lower.endsWith(".tiff");
    },
    getDisplayUrl(type, url) {
      if (!url) return "";
      const converted = this.convertedImageUrls[type];
      if (converted) {
        return converted;
      }
      if (!this.isTifUrl(url)) {
        return url;
      }
      if (!this.convertingMap[type]) {
        this.convertTifImage(type, url);
      }
      return "";
    },
    handleImageSourceChange(type, url) {
      this.$delete(this.conversionErrors, type);
      this.$set(this.convertedImageUrls, type, null);
      if (!url) {
        this.$set(this.convertingMap, type, false);
        return;
      }
      if (this.isTifUrl(url)) {
        this.convertTifImage(type, url);
      } else {
        this.$set(this.convertingMap, type, false);
      }
    },
    async convertTifImage(type, url) {
      if (!url || this.convertingMap[type]) {
        return;
      }
      this.$set(this.convertingMap, type, true);
      this.$delete(this.conversionErrors, type);
      try {
        const isMobile = window.innerWidth <= 768;

        // 手机端增加超时时间
        const controller = new AbortController();
        const timeoutId = setTimeout(
          () => controller.abort(),
          isMobile ? 30000 : 15000
        );

        const response = await fetch(url, {
          cache: "no-store",
          signal: controller.signal,
        });
        clearTimeout(timeoutId);

        if (!response.ok) {
          throw new Error(`Failed to fetch image: ${response.statusText}`);
        }
        const arrayBuffer = await response.arrayBuffer();
        const ifds = UTIF.decode(arrayBuffer);
        if (!ifds.length) {
          throw new Error("No image data found in TIF file");
        }
        const firstImage = ifds[0];
        UTIF.decodeImage(arrayBuffer, firstImage);
        let { width, height } = firstImage;
        if (!width || !height) {
          throw new Error("Invalid TIF image size");
        }

        // 手机端：检查并限制canvas大小，避免超出浏览器限制
        const maxCanvasSize = isMobile ? 2048 : 4096; // 手机端限制更小
        let scale = 1;
        if (width > maxCanvasSize || height > maxCanvasSize) {
          scale = Math.min(maxCanvasSize / width, maxCanvasSize / height);
          width = Math.floor(width * scale);
          height = Math.floor(height * scale);
        }

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");

        // 检查canvas是否创建成功（某些浏览器可能有限制）
        if (!ctx) {
          throw new Error("无法创建canvas上下文");
        }

        // 如果图片太大需要缩放，直接缩放RGBA数据（避免创建超大canvas）
        const imageData = ctx.createImageData(width, height);
        const rgba = UTIF.toRGBA8(firstImage);

        if (scale < 1) {
          // 缩放RGBA数据：使用最近邻采样
          const originalWidth = firstImage.width;
          const originalHeight = firstImage.height;
          const invScale = 1 / scale;

          for (let y = 0; y < height; y++) {
            for (let x = 0; x < width; x++) {
              // 计算原始图片中的位置
              const srcX = Math.min(
                Math.floor(x * invScale),
                originalWidth - 1
              );
              const srcY = Math.min(
                Math.floor(y * invScale),
                originalHeight - 1
              );

              // 计算索引
              const srcIdx = (srcY * originalWidth + srcX) * 4;
              const dstIdx = (y * width + x) * 4;

              // 复制RGBA值
              imageData.data[dstIdx] = rgba[srcIdx];
              imageData.data[dstIdx + 1] = rgba[srcIdx + 1];
              imageData.data[dstIdx + 2] = rgba[srcIdx + 2];
              imageData.data[dstIdx + 3] = rgba[srcIdx + 3];
            }
          }
        } else {
          // 不需要缩放，直接使用
          imageData.data.set(rgba);
        }

        ctx.putImageData(imageData, 0, 0);

        // PNG格式不支持quality参数，直接使用PNG
        const dataUrl = canvas.toDataURL("image/png");

        // 清理临时数据
        canvas.width = 0;
        canvas.height = 0;

        // 使用$set确保响应式更新，并强制触发视图更新
        this.$set(this.convertedImageUrls, type, dataUrl);

        // 手机端：强制更新视图，确保图片显示
        const isMobileForUpdate = window.innerWidth <= 768;
        if (isMobileForUpdate) {
          this.$nextTick(() => {
            this.$forceUpdate();
          });
        }
      } catch (error) {
        console.error(`Failed to convert TIF image (${type}):`, error);
        this.$set(
          this.conversionErrors,
          type,
          (error && error.message) || "convert failed"
        );
        this.$set(this.convertedImageUrls, type, null);
      } finally {
        this.$set(this.convertingMap, type, false);
      }
    },
    togglePrintingState(isPrinting) {
      this.isPrinting = isPrinting;
    },
    getChartInstances() {
      const instances = [];
      if (this.$refs.cvChartCombined) {
        instances.push(this.$refs.cvChartCombined.chart);
      }
      if (this.$refs.SNR_Chart1) {
        instances.push(this.$refs.SNR_Chart1.chart);
      }
      if (this.$refs.SNR_Chart2) {
        instances.push(this.$refs.SNR_Chart2.chart);
      }
      return instances;
    },
    ensureChartsRendered() {
      const chartInstances = this.getChartInstances();
      if (!chartInstances || chartInstances.length === 0) {
        return Promise.resolve();
      }
      const promises = chartInstances.map(
        (chart) =>
          new Promise((resolve) => {
            const listener = () => {
              resolve();
              chart.off("finished", listener);
            };
            chart.on("finished", listener);
            setTimeout(() => {
              resolve();
              chart.off("finished", listener);
            }, 2000);
          })
      );
      return Promise.all(promises);
    },
    scrollToSection(sectionId) {
      const scrollContainer = this.$el.closest(
        ".detail-scrollbar .el-scrollbar__wrap"
      );
      if (!scrollContainer) return;

      if (sectionId === "top") {
        scrollContainer.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        const targetElement = this.$el.querySelector(`#${sectionId}`);
        if (targetElement) {
          targetElement.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }
    },
  },
};
</script>

<style scoped>
.custom-table ::v-deep .el-table__cell {
  padding: 8px 0;
}
.color-report-detail-container {
  padding-right: 15px;
}
.report-header-images {
  display: flex;
  gap: 20px;
  margin-bottom: 25px;
  justify-content: center;
}
.labeled-image-container {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.image-label-wrapper {
  padding: 0 5px;
  text-align: left;
}
.image-wrapper {
  border: 1px solid #e6ebf5;
  border-radius: 6px;
  padding: 20px;
  background-color: #fcfcfc;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 160px;
}
.image-wrapper img {
  width: 100%;
  height: auto;
  border-radius: 4px;
  display: block;
}
.image-loading,
.image-error {
  text-align: center;
  width: 100%;
  color: #909399;
  font-size: 14px;
  display: flex;
  justify-content: center;
  align-items: center;
}
.image-loading .el-icon-loading {
  font-size: 24px;
  color: #409eff;
}
.image-error {
  color: #f56c6c;
}
.analysis-placeholder {
  width: 100%;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 30px auto;
  border-radius: 4px;
}
.analysis-placeholder .el-icon-loading {
  font-size: 26px;
  color: #409eff;
}
.section-title {
  font-size: 20px;
  font-weight: 600;
  color: #303133;
  margin-top: 35px;
  margin-bottom: 20px;
}
.sub-section-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin-top: 25px;
  margin-bottom: 15px;
  padding-left: 12px;
  border-left: 4px solid #00a0e9;
}
.content-divider {
  height: 1px;
  background-color: #e6ebf5;
  margin: 20px 0;
}
.side-by-side-tables {
  display: flex;
  gap: 20px;
}
.chart-container {
  width: 100%;
  margin-bottom: 20px;
  padding: 5px;
  border: 1px solid #e6ebf5;
  border-radius: 6px;
  box-sizing: border-box;
}
.fitting-curve-container {
  margin-top: 30px;
}
.chart-title-text {
  text-align: center;
  font-size: 16px;
  font-weight: normal;
  color: #606266;
  margin-bottom: 15px;
}
.echart-wrapper {
  max-width: 60%;
  margin: 0 auto;
  aspect-ratio: 1 / 1;
}
.chart-stack-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  /* height: 550px; */
}
.echart-on-top {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
.chart-background-image {
  position: absolute;
  left: -15.5%;
  top: 12.5%;
  width: 115%;
  height: auto; /* 高度自动，保持原始比例 */
  z-index: 2;
}
.chart-stack-wrapper .echart-on-top:first-of-type {
  z-index: 1;
}
.chart-stack-wrapper .echart-on-top:last-of-type {
  z-index: 3;
}

.echart {
  height: 500px;
}
.chart-inner-wrapper {
  max-width: 100%;
  margin: 0 auto;
}

.custom-table ::v-deep th {
  background-color: #f5f7fa !important;
  color: #606266;
  font-weight: 600;
}
.custom-table ::v-deep td,
.custom-table ::v-deep th {
  text-align: center;
}
.color-block-image {
  width: 85%;
  height: auto;
  border-radius: 4px;
  display: block;
  max-width: 450px;
  margin: 30px auto;
}
.result-block {
  background-color: #f5f7fa;
  padding: 15px 20px;
  border-radius: 4px;
  margin-top: 20px;
  margin-bottom: 40px;
  color: #303133;
}
.result-block p {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
}
@media (max-width: 768px) {
  .report-header-images {
    flex-direction: column;
  }
  .image-wrapper {
    padding: 15px;
  }

  .side-by-side-tables {
    flex-direction: column;
  }

  /* 优化图表容器在移动端的显示 */
  .echart-wrapper {
    max-width: 95%;
    margin: 0 auto 20px auto;
  }

  .chart-container {
    padding: 10px 5px;
    margin-bottom: 30px;
  }

  .chart-title-text {
    margin-bottom: 20px;
    font-size: 14px;
  }
}
</style>
